import moment from "moment";
import React, { useEffect, useState } from "react";

import {
  allottedTimeFactory,
  timeSlotArray,
} from "@/components/Provider/ScheduleAvailability/constants";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import DatePicker from "@/components/UI/DatePicker";
import { eventTypeDisplayNameFactory } from "@/components/Provider/ScheduleAvailability/ActiveScheduleDetails";
import { formatAllottedTime } from "@/features/notes/helpers/format-time";
import InputError from "@/components/UI/InputError";
import { ProviderEnum } from "@/core/constants";
import { Select } from "@/components/UI/StyledInput";
import { serviceLocationOptions } from "@/features/notes/helpers/service-location-options";
import { sharedRequests } from "@/api/(requests)/shared";
import { testLocator } from "@/lib/helpers/test-locator";
import { transformTimeSlotArray } from "@/components/Provider/ScheduleAvailability/ScheduleEvent";

export function NotesLinkCall(props) {
  //completedCalls indicates that the note is being created manually
  const {
    completedCalls,
    cptOptions,
    editHandler,
    errors,
    handleCallTypeChange,
    initialPatientQuestionSets,
    lastNotesOther,
    note,
    patient,
    resetValidationHandler,
    workflow,
  } = props;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [timeSlotFromOptions, setTimeSlotFromOptions] = useState([]);
  const [timeSlotToOptions, setTimeSlotToOptions] = useState([]);

  useEffect(() => {
    setSessionTimeslotOptions(note.session_from, note.call_type);
  }, []);

  const setSessionTimeslotOptions = (sessionFromStr, callType) => {
    let newTimeSlotOptions = transformTimeSlotArray([...timeSlotArray]);
    const sessionFrom = moment(sessionFromStr, "HH:mm");
    const sessionTo = moment(sessionFromStr, "HH:mm").add(allottedTimeFactory(callType), "minutes");

    newTimeSlotOptions.push(
      { display: sessionFrom.format("hh:mm a"), value: sessionFrom.format("HH:mm") },
      { display: sessionTo.format("hh:mm a"), value: sessionTo.format("HH:mm") },
    );
    setTimeSlotFromOptions(newTimeSlotOptions);
    setTimeSlotToOptions(
      newTimeSlotOptions.filter((option) => option.value > sessionFrom.format("HH:mm")),
    );
  };

  function getNewNoteTitle(sessionDate, sessionFrom, sessionTo) {
    if (note.provider_type.startsWith(ProviderEnum.BEHAVIORAL)) {
      return `Therapy Session ${sessionDate} - (${sessionFrom}-${sessionTo})`;
    } else {
      return `Prescriber Guided Session ${sessionDate} - (${sessionFrom}-${sessionTo})`;
    }
  }

  function updateSessionFields(timestamp, eventType) {
    const newSessionFrom = timestamp.format("HH:mm");
    const newNoteTitle = getNewNoteTitle(
      timestamp.format("MM/DD/YYYY"),
      timestamp.format("hh:mm A"),
      moment(newSessionFrom, "HH:mm")
        .add(allottedTimeFactory(eventType), "minutes")
        .format("hh:mm A"),
    );

    editHandler("call_type", eventType, false);
    editHandler("call_name", eventTypeDisplayNameFactory(eventType), false);

    editHandler("session_date", timestamp.format("MM/DD/YYYY"), false);
    editHandler("session_from", newSessionFrom, false);
    editHandler(
      "session_to",
      moment(newSessionFrom, "HH:mm")
        .add(allottedTimeFactory(eventType), "minutes")
        .format("HH:mm"),
      false,
    );
    editHandler("title", newNoteTitle, false);
    editHandler("call_timer", formatAllottedTime(allottedTimeFactory(eventType)), true);
  }

  function mergeContent(questionSet, note, workflow) {
    const newContent = workflow.details.noteEnricher(
      patient,
      questionSet,
      workflow.details.relevantPatientQuestionSections,
      { existingNote: note, lastNotesOther },
    );

    editHandler("content", newContent, false);
  }

  async function getPatientQuestionSets(callId) {
    const data = {
      call_id: callId,
    };
    const response = await sharedRequests.fetch_twilio_token({ data });

    return (
      Object.hasOwn(response, "call_details") &&
      Object.hasOwn(response.call_details, "patient_question_sets") &&
      response.call_details.patient_question_sets
    );
  }

  function resetSessionToOptions(startTime) {
    const newTimeSlotFromOptions = timeSlotFromOptions.filter((option) => option.value > startTime);

    setTimeSlotToOptions(newTimeSlotFromOptions);

    return newTimeSlotFromOptions;
  }

  const handleCallSelection = async (e) => {
    const call = completedCalls.find((call) => call.value === e.target.value);
    const callId = call ? call.value : null;
    let callContext = { note, questionSet: initialPatientQuestionSets, workflow };
    let date, eventType, sessionFromStr;

    if (callId && callId !== "no_linked_call" && callId !== "non_billable") {
      if (call.event_type !== note.call_type) {
        callContext = await handleCallTypeChange(call.event_type);
      }
      sessionFromStr = moment.utc(call.timestamp, "YYYY-MM-DD HH:mm").local().format("HH:mm");
      const patientQuestionSets = await getPatientQuestionSets(callId);

      if (callContext.workflow.details.noteEnricher && patientQuestionSets) {
        mergeContent(patientQuestionSets, callContext.note, callContext.workflow);
      } else {
        mergeContent(callContext.questionSet, callContext.note, callContext.workflow);
      }
      date = moment.utc(call.timestamp, "YYYY-MM-DD HH:mm").local();
      eventType = call.event_type;
      setSessionTimeslotOptions(sessionFromStr, eventType);
      updateSessionFields(date, eventType);
    } else {
      if (workflow.details.noteEnricher) {
        mergeContent(callContext.questionSet, callContext.note, callContext.workflow);
      }
      eventType = callContext.note.call_type;
    }
    editHandler("call_id", callId, false);
    resetValidationHandler("call_id", "");
  };

  const handleEventTypeSelection = async (e) => {
    const newSessionTo = moment(note.session_from, "HH:mm")
      .add(allottedTimeFactory(e.target.value), "minutes")
      .format("HH:mm");
    const newNoteTitle =
      note.title.split(" - ")[0] +
      ` - (${moment(note["session_from"], "HH:mm").format("hh:mm A")}-${moment(
        newSessionTo,
        "HH:mm",
      ).format("hh:mm A")})`;

    editHandler("call_type", e.target.value, false);
    editHandler("session_to", newSessionTo, false);
    editHandler("title", newNoteTitle, false);
    editHandler("call_name", eventTypeDisplayNameFactory(e.target.value), false);
    editHandler("call_timer", formatAllottedTime(allottedTimeFactory(e.target.value)), true);
    setSessionTimeslotOptions(note.session_from, e.target.value);
    if (!cptOptions.map((option) => option.value).includes(e.target.value)) {
      await handleCallTypeChange(e.target.value);
    }
  };

  const handleSessionDateSelection = (dateStr, eventType) => {
    if (eventType !== "monthChange") {
      setShowDatePicker(false);
    }
    let parts = dateStr.split("-");
    const sessionDate = parts[1] + "/" + parts[2] + "/" + parts[0];

    editHandler("session_date", sessionDate, false);
    editHandler(
      "title",
      getNewNoteTitle(
        sessionDate,
        moment(note.session_from, "HH:mm").format("hh:mm A"),
        moment(note.session_to, "HH:mm").format("hh:mm A"),
      ),
      false,
    );
  };

  const handleSessionFromSelection = (e) => {
    editHandler("session_from", e.target.value, false);
    const sessionFromMoment = moment(e.target.value, "HH:mm");
    const newTimeSlotToOptions = resetSessionToOptions(e.target.value);
    let sessionToMoment;

    if (!newTimeSlotToOptions.map((option) => option.value).includes(note.session_to)) {
      // reset session to so it's not invalid (session_to < session_from)
      sessionToMoment = moment(e.target.value, "HH:mm").add(
        allottedTimeFactory(note.call_type),
        "minutes",
      );
      setSessionTimeslotOptions(e.target.value, note.call_type);
    } else {
      sessionToMoment = moment(note.session_to, "HH:mm");
    }
    editHandler(
      "title",
      getNewNoteTitle(
        note.session_date,
        sessionFromMoment.format("hh:mm A"),
        sessionToMoment.format("hh:mm A"),
      ),
      false,
    );
    editHandler("session_to", sessionToMoment.format("HH:mm"), false);
    editHandler(
      "call_timer",
      formatAllottedTime(sessionToMoment.diff(sessionFromMoment, "minutes")),
      true,
    );
  };

  const handleSessionToSelection = (e) => {
    editHandler("session_to", e.target.value, false);
    const sessionFromMoment = moment(note.session_from, "HH:mm");
    const sessionToMoment = moment(e.target.value, "HH:mm");

    editHandler(
      "title",
      getNewNoteTitle(
        note.session_date,
        sessionFromMoment.format("hh:mm A"),
        sessionToMoment.format("hh:mm A"),
      ),
      false,
    );
    editHandler(
      "call_timer",
      formatAllottedTime(sessionToMoment.diff(sessionFromMoment, "minutes")),
      true,
    );
  };

  const handleServiceLocationSelection = (e) => {
    editHandler("service_location", e.target.value, false);
  };

  return (
    <DashboardCard2>
      <div>
        <div className="clr-sec px-3 py-2 tw:font-bold">Call Linking and Type</div>
      </div>

      <div className="row px-4">
        <div className="col-lg-6 col-12 pb-3">
          <span className="font-weight-bold">Call Link</span>
          <Select
            changeHandler={handleCallSelection}
            name="call_id"
            options={completedCalls}
            testId="callLinkSelect"
            value={note["call_id"]}
          />

          {errors["call_id"] ? (
            <InputError classes="custom-error" testId="callLinkError">
              {errors["call_id"]}
            </InputError>
          ) : (
            ""
          )}
        </div>

        <div className="col-lg-6 col-12 pb-3">
          <span className="font-weight-bold">Select CPT Code</span>
          <Select
            changeHandler={handleEventTypeSelection}
            name="call_type"
            options={cptOptions}
            testId="cptCodeSelect"
            value={note["call_type"]}
          />

          {errors["call_type"] ? (
            <InputError classes="custom-error">{errors["call_type"]}</InputError>
          ) : (
            ""
          )}
        </div>

        <div className="col-lg-6 col-12 pb-3">
          <span className="font-weight-bold">Session Date</span>
          <div className="CustomSelect__wpr d-flex flex-column">
            <button
              className="CustomSelect__btn cursor d-flex align-items-center text-left"
              onClick={() => setShowDatePicker(true)}
              type="button"
            >
              <span>{note.session_date}</span>
            </button>
          </div>

          {showDatePicker && (
            <div className="my-3">
              <DatePicker
                externalCtx={moment(note.session_date, "MM/DD/YYYY")}
                onDaySelect={handleSessionDateSelection}
              />
            </div>
          )}
        </div>

        <div className="col-lg-3 col-6 pb-3">
          <span className="font-weight-bold">Session From</span>
          <Select
            changeHandler={handleSessionFromSelection}
            name="session_from"
            options={timeSlotFromOptions}
            value={note.session_from}
          />
          {errors["session_from"] ? (
            <InputError classes="custom-error">{errors["session_from"]}</InputError>
          ) : (
            ""
          )}
        </div>

        <div className="col-lg-3 col-6 pb-3">
          <span className="font-weight-bold">Session To</span>
          <Select
            changeHandler={handleSessionToSelection}
            name="session_to"
            options={timeSlotToOptions}
            value={note.session_to}
          />
          {errors["session_to"] ? (
            <InputError classes="custom-error">{errors["session_to"]}</InputError>
          ) : (
            ""
          )}
        </div>

        {note.provider_type === ProviderEnum.PRESCRIBE && (
          <div className="col-lg-6 col-12 pb-3">
            <span className="font-weight-bold">Service Location</span>
            <Select
              changeHandler={handleServiceLocationSelection}
              name="service_location"
              options={serviceLocationOptions}
              value={note.service_location}
              {...testLocator("serviceLocationSelect")}
            />
            {errors.service_location ? (
              <InputError classes="custom-error" {...testLocator("serviceLocationError")}>
                {errors.service_location}
              </InputError>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
    </DashboardCard2>
  );
}
